import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  Inject,
  OnInit,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {
  MAT_FORM_FIELD_DEFAULT_OPTIONS,
  MatFormFieldDefaultOptions
} from '@angular/material/form-field';
import {CompositeFilterDescriptor} from '@progress/kendo-data-query/dist/npm/filtering/filter-descriptor.interface';
import {BaseDialogComponent} from '@q9elements/ui-kit/common';
import {
  FilterDescriptors,
  FilterLogic,
  FilterType,
  GRID_FILTER_COMPONENTS,
  GridColumn,
  GridQuery,
  GridUtils
} from '@q9elements/ui-kit/components';
import {differenceBy, find, uniqBy} from 'lodash';

import {GridView} from '../../models/interfaces';
import {GridViewsDaoService} from '../../services/grid-views-dao.service';
import {GridViewsFilterService} from '../../services/grid-views-filter.service';

@Component({
  selector: 'q9ma-edit-filters-dialog',
  templateUrl: './edit-filters-dialog.component.html',
  styleUrls: ['./edit-filters-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    GridViewsFilterService,
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        color: 'primary',
        floatLabel: 'always',
        appearance: 'outline',
        subscriptSizing: 'dynamic',
        hideRequiredMarker: false
      } as MatFormFieldDefaultOptions
    }
  ]
})
export class EditFiltersDialogComponent extends BaseDialogComponent implements OnInit {
  private viewColumns = this.getViewColumns();
  private readonly usedColumns = computed(() =>
    this.viewFilters()
      .map(filter => this.getColumnByField(filter.field))
      .filter(Boolean)
  );

  public readonly viewFilters = computed(() =>
    uniqBy(this.gridViewsFilterService.filters(), 'field')
  );
  public readonly isFiltersValid = this.gridViewsFilterService.isFiltersValid;
  public availableColumns = computed(() =>
    differenceBy(this.viewColumns, this.usedColumns() as GridColumn[], 'field')
  );
  public readonly compositeFilter = computed<CompositeFilterDescriptor>(() => ({
    logic: FilterLogic.AND,
    filters: this.gridViewsFilterService.filters()
  }));
  public readonly templates = inject(GRID_FILTER_COMPONENTS, {optional: true});

  @ViewChild('dialogContent') dialogContent: ElementRef;

  constructor(
    private query: GridQuery,
    @Inject(MAT_DIALOG_DATA) public view: GridView,
    public gridViewsDaoService: GridViewsDaoService,
    public gridViewsFilterService: GridViewsFilterService,
    protected override dialogRef: MatDialogRef<
      EditFiltersDialogComponent,
      {id: string; filters: FilterDescriptors}
    >
  ) {
    super();
  }

  ngOnInit() {
    this.gridViewsFilterService.initFilters(this.view);
  }

  getColumnByField(field: unknown): GridColumn {
    return find(this.viewColumns, {field: String(field)})!;
  }

  addFilter() {
    const [column] = this.availableColumns();

    if (column) {
      this.gridViewsFilterService.addOrReplaceFilter(column.field);
    }

    setTimeout(() => {
      if (this.dialogContent?.nativeElement) {
        this.dialogContent.nativeElement.scrollTo({
          top: this.dialogContent.nativeElement.scrollHeight,
          behavior: 'smooth'
        });
      }
    });
  }

  changeColumn(field: string, index: number) {
    this.gridViewsFilterService.addOrReplaceFilter(field, index);
  }

  removeFilter(index: number, filter: FilterType | undefined) {
    this.gridViewsFilterService.removeFilter(index, filter === FilterType.DATE);
  }

  protected override sendSubmitRequest() {
    const filters = GridUtils.omitFilters(this.gridViewsFilterService.filters());

    return this.gridViewsDaoService.storeFilters(this.view.id, filters);
  }

  override onSubmit() {
    if (!this.view.filters?.length && !this.viewFilters().length) {
      this.error = 'GRID_VIEWS.ADD_FILTER_MSG';

      return;
    }

    if (this.isSubmitted || !this.isFiltersValid()) {
      return;
    }

    this.isSubmitted = true;

    return this.sendSubmitRequest()
      .pipe(this.takeUntilComponentDestroy(), this.handleError())
      .subscribe(filters => {
        this.isSubmitted = false;
        this.dialogRef.close({id: this.view.id, filters});
      });
  }

  private getViewColumns() {
    const allColumns = this.query.getColumns();
    const viewColumns = this.view.columns;

    return allColumns
      .filter(column => viewColumns.includes(column.field))
      .map(column => ({
        ...column,
        filter: column.filter || FilterType.TEXT
      }));
  }

  public trackByFilter(index: number, filter: any): any {
    return filter.id || filter.field || index;
  }

  public trackByColumn(index: number, column: any): any {
    return column.field || index;
  }
}
